import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE, WA_DEFAULT } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
    "CFTV / Câmeras",
    "Controle de acesso facial",
    "Portões e automação",
    "Suporte e manutenção",
    "Outro",
];

const inputCls =
    "mt-2 w-full rounded-xl border border-white/15 bg-ink px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-volt transition-colors duration-200";

const Contact = () => {
    const [form, setForm] = useState({
        nome: "",
        telefone: "",
        condominio: "",
        servico: SERVICE_OPTIONS[0],
        mensagem: "",
    });
    const [sending, setSending] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        if (user) {
            setForm((f) => ({ ...f, nome: f.nome || user.name }));
        }
    }, [user]);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/contato`, form);
            toast.success("Recebemos sua solicitação!", {
                description: "Retornaremos em breve pelo WhatsApp ou telefone informado.",
            });
            setForm({
                nome: "",
                telefone: "",
                condominio: "",
                servico: SERVICE_OPTIONS[0],
                mensagem: "",
            });
        } catch (err) {
            toast.error("Não foi possível enviar agora.", {
                description: "Chame direto no WhatsApp que respondemos rápido.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contato"
            data-testid="contact-section-container"
            className="relative py-24 lg:py-32 bg-panel/40 border-t border-white/10"
        >
            <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
                <Reveal>
                    <Eyebrow>[ 07 — Contato ]</Eyebrow>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] text-white">
                        Vamos proteger
                        <span className="text-volt"> o seu espaço?</span>
                    </h2>
                    <p className="mt-5 max-w-md text-slate-400 leading-relaxed text-sm sm:text-base">
                        Conte o que você precisa e receba uma proposta técnica
                        clara, sem enrolação e sem compromisso.
                    </p>

                    <a
                        href={WA_DEFAULT}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-whatsapp-direct-card"
                        className="group mt-10 flex items-center justify-between gap-5 clip-corner-sm border border-mint/30 bg-mint/5 p-6 hover:bg-mint/10 transition-colors duration-300 max-w-md"
                    >
                        <span className="flex items-center gap-4">
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-mint text-ink">
                                <MessageCircle size={22} strokeWidth={2.4} />
                            </span>
                            <span>
                                <span className="block font-hud text-[10px] uppercase tracking-[0.25em] text-mint">
                                    Resposta rápida
                                </span>
                                <span className="block mt-1 font-display text-lg font-bold text-white">
                                    {SITE.phoneDisplay}
                                </span>
                            </span>
                        </span>
                        <Send
                            size={18}
                            className="text-mint transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                        />
                    </a>
                </Reveal>

                <Reveal delay={0.12}>
                    <form
                        data-testid="contact-form-element"
                        onSubmit={onSubmit}
                        className="clip-corner border border-white/10 bg-panel p-7 sm:p-9"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="ct-name" className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                    Seu nome *
                                </label>
                                <input
                                    id="ct-name"
                                    required
                                    value={form.nome}
                                    onChange={set("nome")}
                                    placeholder="Maria Silva"
                                    data-testid="contact-input-name"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="ct-phone" className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                    WhatsApp / Telefone *
                                </label>
                                <input
                                    id="ct-phone"
                                    required
                                    type="tel"
                                    value={form.telefone}
                                    onChange={set("telefone")}
                                    placeholder="(11) 99999-9999"
                                    data-testid="contact-input-phone"
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        <div className="mt-5 grid sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="ct-condo" className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                    Condomínio / Residência
                                </label>
                                <input
                                    id="ct-condo"
                                    value={form.condominio}
                                    onChange={set("condominio")}
                                    placeholder="Residencial Vista Verde"
                                    data-testid="contact-input-condo-name"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="ct-service" className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                    Serviço
                                </label>
                                <select
                                    id="ct-service"
                                    value={form.servico}
                                    onChange={set("servico")}
                                    data-testid="contact-select-service-type"
                                    className={`${inputCls} appearance-none`}
                                >
                                    {SERVICE_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="mt-5">
                            <label htmlFor="ct-msg" className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Mensagem
                            </label>
                            <textarea
                                id="ct-msg"
                                rows={4}
                                value={form.mensagem}
                                onChange={set("mensagem")}
                                placeholder="Ex.: preciso de 8 câmeras para um condomínio de 2 torres..."
                                data-testid="contact-textarea-message"
                                className={`${inputCls} resize-none`}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="contact-submit-button"
                            className="mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-volt px-6 py-4 font-hud text-xs font-bold uppercase tracking-[0.18em] text-ink hover:bg-mint disabled:opacity-60 transition-colors duration-200"
                        >
                            {sending ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 size={16} />
                                    Enviar solicitação
                                </>
                            )}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};

export default Contact;
